const BASE = '/api'

// --- Cache with stale-while-revalidate ---
const cache = new Map()
const CACHE_TTL = 30 * 1000        // 30s - data considered fresh
const STALE_TTL = 5 * 60 * 1000    // 5min - serve stale while revalidating

// Subscribers: components register callbacks to re-render on data change
const subscribers = new Map() // path -> Set<callback>

function getCached(key) {
  const entry = cache.get(key)
  if (!entry) return null
  const age = Date.now() - entry.timestamp
  if (age > STALE_TTL) {
    cache.delete(key)
    return null
  }
  return { data: entry.data, fresh: age < CACHE_TTL }
}

function setCache(key, data) {
  cache.set(key, { data, timestamp: Date.now() })
  // Notify subscribers
  const subs = subscribers.get(key)
  if (subs) subs.forEach(cb => cb(data))
}

function invalidateCache(...prefixes) {
  for (const key of cache.keys()) {
    if (prefixes.some(p => key.startsWith(p))) {
      cache.delete(key)
    }
  }
}

// Dedup in-flight requests
const inflight = new Map()

async function apiFetch(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options.headers },
  })
  const data = await res.json()
  if (!data.success) throw new Error(data.error || 'API error')
  return data.data
}

async function cachedFetch(path) {
  const cached = getCached(path)
  
  if (cached && cached.fresh) return cached.data
  
  // Stale data exists — return it immediately, revalidate in background
  if (cached && !cached.fresh) {
    revalidate(path)
    return cached.data
  }
  
  // No cache — fetch and wait
  return fetchAndCache(path)
}

async function fetchAndCache(path) {
  // Dedup: if already fetching this path, await same promise
  if (inflight.has(path)) return inflight.get(path)
  
  const promise = apiFetch(path).then(data => {
    setCache(path, data)
    inflight.delete(path)
    return data
  }).catch(err => {
    inflight.delete(path)
    throw err
  })
  
  inflight.set(path, promise)
  return promise
}

function revalidate(path) {
  if (inflight.has(path)) return // already revalidating
  fetchAndCache(path).catch(() => {}) // silent background revalidate
}

// --- Tab visibility: refetch stale data when user switches back ---
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    // Revalidate all cached entries that are stale
    for (const key of cache.keys()) {
      const entry = cache.get(key)
      if (entry && Date.now() - entry.timestamp > CACHE_TTL) {
        revalidate(key)
      }
    }
  }
})

// --- Subscribe: components can listen for data updates ---
export function subscribe(path, callback) {
  if (!subscribers.has(path)) subscribers.set(path, new Set())
  subscribers.get(path).add(callback)
  return () => subscribers.get(path).delete(callback)
}

// --- Force refresh all cached data (call on tab switch in app) ---
export function refreshAll() {
  for (const key of cache.keys()) {
    revalidate(key)
  }
}

// --- Invalidate + immediately refetch ---
function invalidateAndRefetch(...prefixes) {
  const paths = []
  for (const key of cache.keys()) {
    if (prefixes.some(p => key.startsWith(p))) {
      paths.push(key)
    }
  }
  invalidateCache(...prefixes)
  // Refetch after short delay to let writes settle
  setTimeout(() => {
    paths.forEach(p => fetchAndCache(p).catch(() => {}))
  }, 300)
}

export const api = {
  // Work items
  getWorkItems: () => cachedFetch('/work-items'),
  getWorkItem: (id) => apiFetch(`/work-items/${id}`),
  updateWorkItem: (id, body) => {
    invalidateAndRefetch('/work-items', '/dashboard')
    return apiFetch(`/work-items/${id}`, { method: 'PUT', body: JSON.stringify(body) })
  },
  bulkUpdateWorkItems: (items) => {
    invalidateAndRefetch('/work-items', '/dashboard')
    return apiFetch('/work-items/bulk', { method: 'PUT', body: JSON.stringify({ items }) })
  },

  // Executive tracker
  getExecutiveTracker: () => cachedFetch('/executive-tracker'),
  getExecutiveTrackerItem: (id) => apiFetch(`/executive-tracker/${id}`),
  updateExecutiveTracker: (id, body) => {
    invalidateAndRefetch('/executive-tracker', '/dashboard')
    return apiFetch(`/executive-tracker/${id}`, { method: 'PUT', body: JSON.stringify(body) })
  },
  createExecutiveTracker: (body) => {
    invalidateAndRefetch('/executive-tracker', '/dashboard')
    return apiFetch('/executive-tracker', { method: 'POST', body: JSON.stringify(body) })
  },

  // Squad members
  getSquadMembers: () => cachedFetch('/squad-members'),
  getSquadMember: (alias) => apiFetch(`/squad-members/${alias}`),
  updateSquadMember: (alias, body) => {
    invalidateAndRefetch('/squad-members')
    return apiFetch(`/squad-members/${alias}`, { method: 'PUT', body: JSON.stringify(body) })
  },

  // Dashboard
  getDashboardSummary: () => cachedFetch('/dashboard/summary'),

  // User management
  getMe: () => apiFetch('/me'),
  getUsers: () => apiFetch('/users'),
  createUser: (body) => {
    invalidateAndRefetch('/users')
    return apiFetch('/users', { method: 'POST', body: JSON.stringify(body) })
  },
  updateUser: (id, body) => {
    invalidateAndRefetch('/users')
    return apiFetch(`/users/${id}`, { method: 'PUT', body: JSON.stringify(body) })
  },
  deleteUser: (id) => {
    invalidateAndRefetch('/users')
    return apiFetch(`/users/${id}`, { method: 'DELETE' })
  },

  // Feature parity
  getFeatureParity: () => cachedFetch('/feature-parity'),
  updateFeatureParity: (id, body) => {
    invalidateAndRefetch('/feature-parity')
    return apiFetch(`/feature-parity/${id}`, { method: 'PUT', body: JSON.stringify(body) })
  },
  createFeatureParity: (body) => {
    invalidateAndRefetch('/feature-parity')
    return apiFetch('/feature-parity', { method: 'POST', body: JSON.stringify(body) })
  },
  deleteFeatureParity: (id) => {
    invalidateAndRefetch('/feature-parity')
    return apiFetch(`/feature-parity/${id}`, { method: 'DELETE' })
  },

  // Weekly summaries
  getWeeklySummaries: () => cachedFetch('/weekly-summaries'),
  updateWeeklySummary: (week, body) => {
    invalidateAndRefetch('/weekly-summaries')
    return apiFetch(`/weekly-summaries/${week}`, { method: 'PUT', body: JSON.stringify(body) })
  },

  // Force refresh (call from App on tab switch)
  refresh: refreshAll,
}
